import { AppSignType, SignPageOption, SignPositionOnPage, VisualizationPositionOnPage, UserStampType } from '@/models/enums';

const QrCodeImageSizeDefault: number = 25;
const NumberOfQrCodesDrawnNearStampsDefault: number = 2;
const QrCodePositionOnPageDefault: VisualizationPositionOnPage = VisualizationPositionOnPage.LeftBottom;

export class StampQrCodeSize {
  width: number = QrCodeImageSizeDefault;
}

export class StampQrCodeShift {
  left: number = 0;
  right: number = 0;
  top: number = 0;
  bottom: number = 0;
}

export class StampQrCodeField {
  included: boolean = false;
  showAsText: boolean = false;
}

export class StampQrCodeTextField {
  included: boolean = false;
  showAsText: boolean = false;
  text: string = '';
}

export class StampQrCodeFields {
  documentDate?: StampQrCodeField = new StampQrCodeField();
  documentNumber: StampQrCodeField = new StampQrCodeField();
  documentName: StampQrCodeField = new StampQrCodeField();
  signatoryName: StampQrCodeField = new StampQrCodeField();
  signatoryOrganization: StampQrCodeField = new StampQrCodeField();
  signatoryEdrpou: StampQrCodeField = new StampQrCodeField();
  signatureTimeStamp: StampQrCodeField = new StampQrCodeField();
  certificateSerial: StampQrCodeField = new StampQrCodeField();
  certificateExpireDate: StampQrCodeField = new StampQrCodeField();
  documentAccessUrl: StampQrCodeField = new StampQrCodeField();
  additionalText: StampQrCodeTextField = new StampQrCodeTextField();
}

export class StampQrCodeVisualizationParams {
  enabledForCounterparties: boolean = false; // indicates if signs of the counterparties need to be visualized as qr codes
  numberOfQrCodesDrawnNearStamps: number = NumberOfQrCodesDrawnNearStampsDefault; // number of qr codes which can be visualized near the stamps
  position: VisualizationPositionOnPage = QrCodePositionOnPageDefault;
  size: StampQrCodeSize = new StampQrCodeSize();
  shift: StampQrCodeShift = new StampQrCodeShift();
  fields: StampQrCodeFields = new StampQrCodeFields();
}

export class QrCodeVisualizationParamsContainer {
  current: StampQrCodeVisualizationParams;
  previous: StampQrCodeVisualizationParams;
  isChanged: boolean;

  constructor() {
    this.current = new StampQrCodeVisualizationParams();
    this.previous = new StampQrCodeVisualizationParams();
    this.isChanged = false;
  }
}

export class UserStampModel {
  userStampType: UserStampType = UserStampType.None;
  signType: AppSignType = AppSignType.None;
  documentVersionCode: string;
  isEnabled: boolean;
  stamp: string;
  visualizationParams: string;

  constructor(userStampObject: any = {}) {
    if (userStampObject.userStampType) {
      this.userStampType = typeof userStampObject.userStampType === 'string'
        ? UserStampType[userStampObject.userStampType]
        : userStampObject.userStampType;
    }
    if (this.userStampType == null) {
      this.userStampType = UserStampType.None;
    }
    if (userStampObject.signType) {
      this.signType = typeof userStampObject.signType === 'string'
        ? AppSignType[userStampObject.signType]
        : userStampObject.signType;
    }
    if (this.signType == null) {
      this.signType = AppSignType.None;
    }
    this.documentVersionCode = userStampObject.documentVersionCode || '';
    this.isEnabled = userStampObject.isEnabled || false;
    this.stamp = userStampObject.stamp || '';
    this.visualizationParams = userStampObject.visualizationParams || '';
  }
}

export function mapUserStampObjectToModel(userStampObjects: Array<any>): Array<UserStampModel> {
  return (userStampObjects || []).map(uso => new UserStampModel(uso));
}

export function deserializeQrCodeVisualizationParams(visualizationParamsJson: string): StampQrCodeVisualizationParams | undefined {
  let visualizationParams: StampQrCodeVisualizationParams | undefined;
  if (visualizationParamsJson) {
    visualizationParams = Object.assign(
      new StampQrCodeVisualizationParams(),
      upgradeQrCodeVisualizationParamsModel(JSON.parse(visualizationParamsJson))
    );
  }
  return visualizationParams;
}

function upgradeQrCodeVisualizationParamsModel(visualisationParams: any): StampQrCodeVisualizationParams {
  if (visualisationParams) {
    if (!('enabledForCounterparties' in visualisationParams)) {
      visualisationParams.enabledForCounterparties = false;
    }
    if (!('numberOfQrCodesDrawnNearStamps' in visualisationParams)) {
      visualisationParams.numberOfQrCodesDrawnNearStamps = NumberOfQrCodesDrawnNearStampsDefault;
    }
    if (visualisationParams.fields) {
      if (!('certificateExpireDate' in visualisationParams.fields)) {
        visualisationParams.fields.certificateExpireDate = {
          included: false,
          showAsText: false
        };
      }
    }
  }
  return visualisationParams as StampQrCodeVisualizationParams;
}

export class CompoundVisualizationSettings {
  defaults?: UserStampModel;
  document?: UserStampModel;

  constructor(defaults?: UserStampModel, document?: UserStampModel) {
    this.defaults = defaults;
    this.document = document;
  }
}

export class DocumentOption {
  documentSignPositionTypeId: SignPageOption;
  documentSignPositionOnPageId: SignPositionOnPage;

  constructor(documentSignPositionTypeId: SignPageOption = SignPageOption.None,
    documentSignPositionOnPageId: SignPositionOnPage = SignPositionOnPage.AllInLowerLeftCorner) {
    this.documentSignPositionTypeId = documentSignPositionTypeId;
    this.documentSignPositionOnPageId = documentSignPositionOnPageId;
  }
}

export interface IAvailableVisualizationSetting {
  userStampType: UserStampType;
  signType: AppSignType;
  userCode: string;
  unregisteredUserId: number;
  isAvailable: boolean;
  isModuleRequired: boolean;
  isDocumentSettings: boolean;
  isDocumentAuthorSettings: boolean;
  isCurrentUserSettings: boolean;
}

export function mapAvailableVisualizationSettings(data: Array<any>): Array<IAvailableVisualizationSetting> {
  return (data || []).map((d: any) => {
    if (d.signType && typeof d.signType === 'string') {
      d.signType = AppSignType[d.signType];
    }
    if (d.userStampType && typeof d.userStampType === 'string') {
      d.userStampType = UserStampType[d.userStampType];
    }
    return d as IAvailableVisualizationSetting;
  });
}
